import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import Header from 'components/Headers/Header';
import {
  Card,
  CardHeader,
  Container,
  Input,
  InputGroup,
  Row,
  Table,
} from 'reactstrap';
import toast from 'react-hot-toast';
import api from 'services/api';
import { CircularProgress } from '@material-ui/core';
import FloatingButton from 'components/FloatingButton';
import Swal from 'sweetalert2';
import ProdutoAtividade from 'models/ProdutoAtividade';

interface LocationState {
  propostaId: number;
  orcamentoId: number;
  emprCodigo: string;
  geral: boolean | undefined;
}

const NUMERO_MAXIMO_ITENS_AVULSOS = 20;

const ConfiguradorItensAvulsosNovo: React.FC = () => {
  const location = useLocation<LocationState>();
  const propostaVersaoId = location.state?.propostaId ?? null;
  const orcamentoId = location.state?.orcamentoId ?? null;
  const emprCodigo = location.state?.emprCodigo ?? '';
  const geral = location.state?.geral;
  const [atividades, setAtividades] = useState<ProdutoAtividade[]>([]);

  const [materiaisAvulsos, setMateriaisAvulsos] = useState(
    Array.from({ length: NUMERO_MAXIMO_ITENS_AVULSOS }, () => ({
      item: '',
      quantidade: '',
      sequencia: '',
      atividade: '',
    })),
  );

  const [errors, setErrors] = useState<Record<number, string[]>>({});

  const [isLoading, setIsLoading] = useState(false);

  const handleInputChange = (
    index: number,
    campo: keyof typeof materiaisAvulsos[number],
    valor: string,
  ) => {
    const materiaisAvulsosAtualizados = [...materiaisAvulsos];
    materiaisAvulsosAtualizados[index][campo] = valor;
    setMateriaisAvulsos(materiaisAvulsosAtualizados);

    // Retira a mensagem de erro quando começa a digitar no campo
    if (errors[index]) {
      const updatedErrors = { ...errors };
      updatedErrors[index] = updatedErrors[index].filter(
        field => field !== campo,
      );
      if (updatedErrors[index].length === 0) {
        delete updatedErrors[index];
      }
      setErrors(updatedErrors);
    }
  };

  async function validaRows() {
    // coluna e quais campos estão faltando
    const errorsArr: Record<number, string[]> = {};
    let temUmaLinhaValida = false;

    materiaisAvulsos.forEach((material, index) => {
      const temValor = Object.values(material).some(
        value => value && String(value)?.trim() !== '',
      );
      if (temValor) {
        const camposFaltando = Object.keys(material).filter(
          key =>
            material[key as keyof typeof material] &&
            String(material[key as keyof typeof material])?.trim() === '',
        );

        if (camposFaltando.length > 0) {
          errorsArr[index] = camposFaltando;
        } else {
          temUmaLinhaValida = true;
        }
      }
    });

    const itensInvalidos = materiaisAvulsos.filter(
      item =>
        item.item && (!item.sequencia || !item.atividade || !item.quantidade),
    );

    setErrors(errorsArr);

    if (!temUmaLinhaValida) {
      toast.error('Não há nenhum material para adicionar.');
      setIsLoading(false);
      return;
    }

    if (Object.keys(errorsArr).length > 0 || itensInvalidos.length > 0) {
      toast.error('Alguns campos não foram preenchidos');
      setIsLoading(false);
      return;
    }

    const url =
      propostaVersaoId && propostaVersaoId !== 0
        ? `/integracao/item-lote`
        : `/integracao/orcamento/item-lote`;

    Swal.fire({
      title: `Deseja incluir os materiais?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: `Sim`,
      confirmButtonColor: '#d33',
      cancelButtonText: `Não`,
    }).then(async result => {
      if (result.isConfirmed) {
        setIsLoading(true);
        try {
          const response = await api.post(`${url}`, {
            rows: materiaisAvulsos.map(it => {
              return {
                ...it,
                item: String(it.item).trim(),
              };
            }),
            emprCodigo: emprCodigo ?? '',
            geral,
            propostaId: propostaVersaoId,
            orcamentoId,
          });

          const retorno: {
            sucesso: boolean | false;
            totalSolicitado: number;
            totalSucesso: number;
            itensValidos: any[];
            itensInvalidos: any[];
          } = response.data;

          setIsLoading(false);
          if (retorno.sucesso) {
            setMateriaisAvulsos(
              Array.from({ length: NUMERO_MAXIMO_ITENS_AVULSOS }, () => ({
                item: '',
                quantidade: '',
                sequencia: '',
                atividade: '',
              })),
            );
            toast.success('Materiais adicionados com sucesso');
          } else {
            Swal.fire({
              icon: 'success',
              title: `${retorno.totalSucesso} de ${retorno.totalSolicitado} materiais foram inseridos.`,
              text: `Os demais, não tiveram sucesso. Eles ficaram na tabela para avaliação. Verifique se o código está correto e se o item está ativo.`,
            });

            setMateriaisAvulsos([
              ...retorno.itensInvalidos?.map((item: any) => {
                return {
                  item: item.codigo,
                  quantidade: item.quantidade,
                  atividade: item.atividade,
                  sequencia: item.sequencia,
                };
              }),
              ...Array.from(
                {
                  length:
                    NUMERO_MAXIMO_ITENS_AVULSOS -
                    (retorno.totalSolicitado - retorno.totalSucesso),
                },
                () => ({
                  item: '',
                  quantidade: '',
                  atividade: '',
                  sequencia: '',
                }),
              ),
            ]);
          }

          setIsLoading(false);
        } catch (error) {
          setIsLoading(false);
          toast.error('Erro ao salvar os materiais.');
        }
      }
    });
  }

  return (
    <>
      <Header showCards={false} />
      <Container className="mt--7" fluid>
        <Row>
          <div className="col">
            <Card className="shadow">
              <CardHeader className="border-0 d-flex justify-content-between align-items-center">
                <h3 className="mb-0">Cadastro de materiais avulsos em lote</h3>

                <FloatingButton
                  disabled={isLoading}
                  color="primary"
                  onClick={validaRows}
                  className="custom-button"
                >
                  <>
                    <small className="p-2">Incluir</small>
                    {isLoading && (
                      <CircularProgress
                        className="mt-2 ml-2"
                        color="inherit"
                        size={10}
                      />
                    )}
                  </>
                </FloatingButton>
              </CardHeader>
              <Table bordered responsive>
                <thead>
                  <tr>
                    <th>Item</th>
                    <th>Quantidade</th>
                    <th>Sequência</th>
                    <th>Atividade</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <tr>
                      <td colSpan={3} className="text-center">
                        <CircularProgress color="inherit" size={30} />
                      </td>
                    </tr>
                  ) : (
                    materiaisAvulsos.map((material, index) => (
                      <tr key={index}>
                        <td>
                          <InputGroup className="input-group-alternative">
                            <Input
                              type="text"
                              value={material.item}
                              onChange={e =>
                                handleInputChange(index, 'item', e.target.value)
                              }
                            />
                          </InputGroup>
                          {errors[index]?.includes('item') && (
                            <div className="text-danger">
                              Item é obrigatório
                            </div>
                          )}
                        </td>
                        <td>
                          <InputGroup className="input-group-alternative">
                            <Input
                              type="number"
                              value={material.quantidade}
                              onChange={e =>
                                handleInputChange(
                                  index,
                                  'quantidade',
                                  e.target.value,
                                )
                              }
                            />
                          </InputGroup>
                          {errors[index]?.includes('quantidade') && (
                            <div className="text-danger">
                              Quantidade é obrigatório
                            </div>
                          )}
                        </td>
                        <td>
                          <InputGroup className="input-group-alternative">
                            <Input
                              type="number"
                              value={material.sequencia}
                              onChange={e =>
                                handleInputChange(
                                  index,
                                  'sequencia',
                                  e.target.value,
                                )
                              }
                            />
                          </InputGroup>
                          {errors[index]?.includes('sequencia') && (
                            <div className="text-danger">
                              Sequencia é obrigatório
                            </div>
                          )}
                        </td>
                        <td>
                          <InputGroup className="input-group-alternative">
                            <Input
                              type="text"
                              value={material.atividade}
                              onChange={e =>
                                handleInputChange(
                                  index,
                                  'atividade',
                                  e.target.value,
                                )
                              }
                            />
                          </InputGroup>
                          {errors[index]?.includes('atividade') && (
                            <div className="text-danger">
                              Atividade é obrigatório
                            </div>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </Card>
          </div>
        </Row>
      </Container>
    </>
  );
};

export default ConfiguradorItensAvulsosNovo;
